// Helper utility functions

/**
 * Format a UTC instant from the API for display in the user's browser timezone.
 * Naive ISO strings (no Z / offset) are treated as UTC — FastAPI often emitted those for Mongo datetimes.
 */
export const formatUtcInstantForDisplay = (value) => {
  if (value == null || value === '') return null;
  const raw = typeof value === 'string' ? value.trim() : value;
  if (typeof raw === 'string') {
    const m = raw.match(/^(\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2})(\.\d+)?$/);
    if (m) {
      // Treat naive timestamps from API as UTC whether they use "T" or space separator.
      const isoBase = m[1].replace(' ', 'T');
      const d = new Date(`${isoBase}Z`);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
      }
    }
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return typeof raw === 'string' ? raw : '—';
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

export const formatReadableProjectId = (projectId) => {
  if (!projectId) return '—';
  const cleaned = String(projectId).replace(/^proj_/, '');
  const compact = cleaned.replace(/-/g, '');
  const suffix = compact.slice(-8).toUpperCase();
  return `PRJ-${suffix || 'UNKNOWN'}`;
};

/** Display project names with an initial capital letter (e.g. test1 → Test1). */
export const formatProjectDisplayName = (name, { fallback = '—' } = {}) => {
  const raw = String(name ?? '').trim();
  if (!raw) return fallback;
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

/**
 * Display status values with a capital first letter on each word.
 * e.g. completed -> Completed, in_progress -> In Progress
 */
/**
 * Rewrite an absolute platform API URL to a same-origin /api path.
 * Only for local starter-kit / dev hosts that proxy /api to the backend.
 * On production UI hosts (e.g. enterpriseaihub...), /api is the SPA — rewriting
 * would fetch index.html and break PDF/text preview. Keep the absolute backend URL.
 */
export const toSameOriginApiPath = (url) => {
  if (!url || typeof url !== 'string') return url;
  const trimmed = url.trim();
  if (!trimmed) return trimmed;

  const host = typeof window !== 'undefined' ? String(window.location.hostname || '') : '';
  const isLocalProxyHost =
    host === 'localhost'
    || host === '127.0.0.1'
    || host === '0.0.0.0'
    || host.endsWith('.localhost');
  if (!isLocalProxyHost) {
    return trimmed;
  }

  if (trimmed.startsWith('/api/')) return trimmed;
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const parsed = new URL(trimmed, base);
    if (parsed.pathname.startsWith('/api/')) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
  } catch {
    // keep original when not a valid URL
  }
  return trimmed;
};

export const formatStatusLabel = (value, { fallback = '—' } = {}) => {
  const raw = String(value ?? '').trim();
  if (!raw) return fallback;
  const lower = raw.toLowerCase();
  if (lower === 'deleting') return 'Deleting';
  return raw
    .replace(/-/g, '_')
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

export const helpers = {
  formatReadableProjectId,
  formatUtcInstantForDisplay,
  formatProjectDisplayName,
  formatStatusLabel,
  toSameOriginApiPath,
};
